import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Users, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { eventsApi } from '../../lib/api';

const GOLD = '#D4AF37';

/**
 * Virtual queue gate. Premium + festive: event flyer as cinematic background,
 * glassmorphism gold card, animated rings + progress, ticker of perks.
 *
 *  - Backend instantly returns "ready" when queue_enabled=false → safe to mount.
 *  - Persists token in localStorage so refreshes don't lose position.
 *  - Soft-fails (treats as ready) if the API errors so users aren't locked out.
 */
export default function VirtualQueueGate({ eventId, eventTitle, eventImage, onReady }) {
  const [status, setStatus] = useState('joining');
  const [position, setPosition] = useState(null);
  const [totalInQueue, setTotalInQueue] = useState(0);
  const [estimatedWait, setEstimatedWait] = useState(0);
  const [maxPositionSeen, setMaxPositionSeen] = useState(null);
  const tokenRef = useRef(localStorage.getItem(`queue_token_${eventId}`));
  const pollRef = useRef(null);
  const readyFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const finalize = (st) => {
      if (cancelled) return;
      setStatus(st);
      if (st === 'ready' && !readyFiredRef.current) {
        readyFiredRef.current = true;
        // Brief beat so the gate is perceivable when it actually fired
        setTimeout(() => onReady?.(), maxPositionSeen ? 700 : 0);
      }
    };

    const poll = async () => {
      if (!tokenRef.current) return;
      try {
        const r = await eventsApi.checkQueue(eventId, tokenRef.current);
        const data = r.data || {};
        if (cancelled) return;
        const pos = data.position || 0;
        setPosition(pos);
        setTotalInQueue(data.total_in_queue || 0);
        setEstimatedWait(data.estimated_wait || 0);
        setMaxPositionSeen((prev) => (prev == null ? pos : Math.max(prev, pos)));
        if (data.status === 'ready') finalize('ready');
        else if (data.status === 'sold_out') finalize('sold_out');
        else setStatus('waiting');
      } catch (_) {
        finalize('ready');
      }
    };

    const join = async () => {
      try {
        if (!tokenRef.current) {
          const r = await eventsApi.joinQueue(eventId);
          const data = r.data || {};
          tokenRef.current = data.token;
          if (data.token) localStorage.setItem(`queue_token_${eventId}`, data.token);
          const pos = data.position || 0;
          setPosition(pos);
          setMaxPositionSeen(pos);
          if (data.status === 'ready') return finalize('ready');
        }
        await poll();
        pollRef.current = setInterval(poll, 4000);
      } catch (_) {
        finalize('ready');
      }
    };
    join();
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  if (status === 'ready') return null;

  const minutes = Math.max(0, Math.round((estimatedWait || 0) / 60));
  const progress = maxPositionSeen && position
    ? Math.max(0.04, Math.min(0.96, 1 - position / Math.max(1, maxPositionSeen)))
    : 0.04;

  return (
    <div className="fixed inset-0 z-[200] overflow-hidden" data-testid="virtual-queue-gate">
      {/* Cinematic flyer background */}
      <div className="absolute inset-0">
        {eventImage ? (
          <>
            <motion.div
              initial={{ scale: 1.06, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${eventImage})` }}
            />
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(circle at 50% 38%, rgba(0,0,0,0.35), rgba(0,0,0,0.85) 70%)',
            }} />
            <div className="absolute inset-0 backdrop-blur-2xl bg-black/55" />
          </>
        ) : (
          <div className="absolute inset-0 bg-black" />
        )}
        {/* Gold halo */}
        <motion.div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-[180px] pointer-events-none"
          style={{ background: `${GOLD}30` }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.55, 0.85, 0.55] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-white text-center">
        {status === 'sold_out' ? (
          <SoldOutCard title={eventTitle} />
        ) : (
          <>
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/12 backdrop-blur-md mb-6"
            >
              <Sparkles size={11} className="text-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#D4AF37]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                Sala de espera Bravo
              </span>
            </motion.div>

            {/* Animated ring + position */}
            <div className="relative mb-6">
              <RingDial value={position || 1} maxValue={Math.max(1, maxPositionSeen || 1)} status={status} />
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-5xl font-black tracking-tighter leading-[0.92] uppercase max-w-2xl"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
              data-testid="virtual-queue-headline"
            >
              {status === 'joining'
                ? 'Sumándote a la cola…'
                : position && position <= 5
                  ? 'Estás por entrar'
                  : 'Mantenete acá, ya casi'}
            </motion.h1>

            {eventTitle && (
              <p className="mt-3 text-sm sm:text-base text-white/65 max-w-md mx-auto">
                <span className="text-white font-bold">{eventTitle}</span> tiene mucha demanda. Te aseguramos un lugar justo en la fila.
              </p>
            )}

            {/* Stats glass card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 grid grid-cols-2 gap-3 w-full max-w-md"
              data-testid="virtual-queue-stats"
            >
              <GlassStat
                icon={Users}
                label="Tu posición"
                value={position ? `#${position}` : '—'}
                accent
              />
              <GlassStat
                icon={Clock}
                label="Estimado"
                value={minutes > 0 ? `~${minutes} min` : '< 1 min'}
              />
            </motion.div>

            {/* Progress bar */}
            <div className="mt-6 w-full max-w-md">
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${GOLD}, #F5D25A)` }}
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
              <p className="text-[10px] uppercase tracking-[0.24em] text-white/45 mt-2">
                {totalInQueue > 0 ? `${totalInQueue} ${totalInQueue === 1 ? 'persona' : 'personas'} en cola` : 'Buscando tu lugar…'}
                <span className="text-white/30"> · refresca cada 4s</span>
              </p>
            </div>

            {/* Bottom ticker */}
            <div className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/45">
              <ShieldCheck size={13} className="text-[#D4AF37]" /> No cierres esta ventana — la fila avanza sola
            </div>

            {status === 'joining' && (
              <div className="mt-4 inline-flex items-center gap-2 text-[10px] text-white/40">
                <Loader2 size={11} className="animate-spin" /> Buscando tu posición
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RingDial({ value, maxValue, status }) {
  const size = 168;
  const stroke = 4;
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  // Fill represents how close you are: as position decreases relative to max, ring fills
  const ratio = Math.max(0.04, Math.min(0.99, 1 - (value || 1) / Math.max(1, maxValue || 1)));
  const dashOffset = c - c * ratio;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Pulsing outer halo */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{ boxShadow: `0 0 60px 12px ${GOLD}28` }}
        animate={{ opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={GOLD}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.0, ease: 'easeOut' }}
        />
      </svg>
      {/* Number */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[9px] uppercase tracking-[0.32em] text-white/55 font-bold">tu lugar</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={value}
            initial={{ opacity: 0, y: 8, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.92 }}
            transition={{ duration: 0.35 }}
            className="text-5xl sm:text-6xl font-black mt-1"
            style={{ color: GOLD, fontFamily: "'Unbounded', sans-serif", textShadow: `0 4px 22px ${GOLD}50` }}
          >
            {status === 'joining' ? '…' : `#${value || '—'}`}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

function GlassStat({ icon: Icon, label, value, accent = false }) {
  return (
    <div
      className={`rounded-2xl p-4 border backdrop-blur-xl ${
        accent
          ? 'border-[#D4AF37]/45 bg-[#D4AF37]/12'
          : 'border-white/15 bg-white/[0.06]'
      }`}
      style={{ boxShadow: accent ? `0 12px 40px -16px ${GOLD}50` : '0 8px 30px -20px rgba(0,0,0,0.6)' }}
    >
      <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-wider text-white/55 mb-1">
        <Icon size={11} /> {label}
      </div>
      <p
        className={`font-black tabular-nums ${
          accent ? 'text-[#D4AF37] text-3xl sm:text-4xl' : 'text-white text-2xl sm:text-3xl'
        }`}
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {value}
      </p>
    </div>
  );
}

function SoldOutCard({ title }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-md text-center px-6 py-8 rounded-3xl border border-white/15 bg-black/55 backdrop-blur-xl"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-red-300 mb-3">Agotado</p>
      <h1
        className="text-3xl sm:text-4xl font-black tracking-tighter uppercase mb-3"
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        Se vendieron todas
      </h1>
      <p className="text-sm text-white/65">
        Las entradas para <span className="text-white font-bold">{title}</span> ya se agotaron. Seguinos para enterarte si liberamos lugares.
      </p>
    </motion.div>
  );
}
